import * as React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import Copyright from '../dashboard/internals/components/Copyright';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxWidth: '600px',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.surface,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderStyle: 'none',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const PolicyContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  [theme.breakpoints.down('sm')]: {
    padding: '20px 0',
    backgroundColor: 'transparent',
  },
  minHeight: '100vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: theme.palette.mode === 'light'
      ? 'url(/light-bg.jpg)'
      : 'url(/dark-bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  },
}));

export interface PolicyPageProps {
  title: string;
  /** Policy text (loaded from blob or fallback). Null while loading or on error. */
  content: string | null;
  /** Whether the policy text is still loading */
  loading?: boolean;
  /** Error message if loading failed */
  error?: string | null;
  /** URL to the PDF file (e.g. in public folder: /terms.pdf) */
  pdfUrl: string;
  disableCustomTheme?: boolean;
}

export default function PolicyPage({
  title,
  content,
  loading = false,
  error = null,
  pdfUrl,
  disableCustomTheme,
}: PolicyPageProps) {
  return (
    <AppTheme disableCustomTheme={disableCustomTheme}>
      <CssBaseline enableColorScheme />
      <PolicyContainer direction="column" justifyContent="space-between">
        <Box sx={{ flexGrow: 1, mt: '10vh' }}>
          <Card variant="outlined">
            <Typography
              sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxHeight: '60vh',
                overflow: 'auto',
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                minHeight: 200,
              }}
            >
              {loading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                  }}
                >
                  <CircularProgress aria-label="Loading policy" />
                </Box>
              )}
              {error && !loading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                  }}
                >
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                </Box>
              )}
              {content && !loading && (
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    width: '100%',
                    display: 'block',
                  }}
                >
                  {content}
                </Typography>
              )}
            </Box>
            <Typography sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                href={pdfUrl}
                variant="body2"
                download
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline' }}
              >
                Download PDF
              </Link>
            </Typography>
          </Card>
        </Box>
        <Copyright sx={{ mt: 2, mb: 2 }} />
      </PolicyContainer>
    </AppTheme>
  );
}
